import { HttpClient } from '@angular/common/http';
import { HrConfigurationService } from 'humanresources-common-shared';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { PerWorkSubareaQueryModel } from '../model/per-work-subarea-query-model';
import { PerWorkSubareaModel } from '../model/per-work-subarea-model';
import { HvlPage, HvlResponse } from '@hvlng/framework-bff/core';
import { map } from 'rxjs/operators';
import { JsonConvert } from 'json2typescript';
import { LookupModel } from 'infrastructure-shared';

@Injectable({
    providedIn: 'root',
})
export class PerWorkSubareaService {
    endpoint: string;

    constructor(private httpClient: HttpClient, private configService: HrConfigurationService) {
        this.endpoint = this.configService.config.hr.general + '/workSubareaService/workSubArea/';
    }

    public delete(id: number): Observable<any> {
        return this.httpClient.delete(this.endpoint + id);
    }

    public queryPage(pagination: PerWorkSubareaQueryModel): Observable<HvlPage<Array<PerWorkSubareaModel>>> {
        return this.httpClient
            .post<HvlResponse<HvlPage<PerWorkSubareaModel[]>>>(this.endpoint + 'page', pagination)
            .pipe(
                map((hvlPage) => {
                    let model = [];
                    if (hvlPage.body?.totalElements !== 0) {
                        const converter = new JsonConvert();
                        model = converter.deserializeArray(hvlPage.body.data, PerWorkSubareaModel);
                    }
                    return new HvlPage<Array<PerWorkSubareaModel>>(
                        model,
                        hvlPage.body.totalElements,
                        hvlPage.body.totalPages,
                        hvlPage.errorDetail,
                    );
                }),
            );
    }

    public update(model: PerWorkSubareaModel): Observable<PerWorkSubareaModel> {
        const converter = new JsonConvert();
        return this.httpClient
            .put<HvlResponse<PerWorkSubareaModel>>(this.endpoint, converter.serialize(model, PerWorkSubareaModel))
            .pipe(
                map((data) => {
                    const converter = new JsonConvert();
                    return converter.deserializeObject(data.body, PerWorkSubareaModel);
                }),
            );
    }

    public getById(id: number): Observable<PerWorkSubareaModel> {
        return this.httpClient.get<HvlResponse<PerWorkSubareaModel>>(this.endpoint + id).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeObject(data.body, PerWorkSubareaModel);
            }),
        );
    }

    public create(model: PerWorkSubareaModel): Observable<PerWorkSubareaModel> {
        const converter = new JsonConvert();
        return this.httpClient
            .post<HvlResponse<PerWorkSubareaModel>>(this.endpoint, converter.serialize(model, PerWorkSubareaModel))
            .pipe(
                map((data) => {
                    return converter.deserializeObject(data.body, PerWorkSubareaModel);
                }),
            );
    }

    public queryList(queryModel: PerWorkSubareaQueryModel): Observable<Array<PerWorkSubareaModel>> {
        return this.httpClient.post<HvlResponse<Array<PerWorkSubareaModel>>>(this.endpoint + 'query', queryModel).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeArray(data.body || [], PerWorkSubareaModel);
            }),
        );
    }

    public queryListByLocale(
        queryModel: PerWorkSubareaQueryModel,
        locale: string,
    ): Observable<Array<PerWorkSubareaModel>> {
        return this.httpClient
            .post<HvlResponse<Array<PerWorkSubareaModel>>>(this.endpoint + 'query/byLocale/' + locale, queryModel)
            .pipe(
                map((data) => {
                    const converter = new JsonConvert();
                    return converter.deserializeArray(data.body || [], PerWorkSubareaModel);
                }),
            );
    }

    public lookupQuery(queryModel: PerWorkSubareaQueryModel): Observable<Array<LookupModel>> {
        return this.httpClient.post<HvlResponse<Array<LookupModel>>>(this.endpoint + 'lookup/query', queryModel).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeArray(data.body || [], LookupModel);
            }),
        );
    }
}
